/* import __COLOCATED_TEMPLATE__ from './seller-profile-notes-item.hbs'; */
import { Promise } from 'rsvp';
import { computed, defineProperty } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import {
  convertBRsToNewLines,
  convertNewLinesToBRs,
  stripTags
} from "../../utils/misc";
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { showModal } from "sh-www/utils/modal-controls";

export default Component.extend({
  session: service(),
  currentUser: readOnly('session.currentUser'),

  /**
   * HTML Component properties
   */
  tagName: 'li',
  classNames: ['SH-c-seller-profile-notes-item', 'margin-top-16px'],
  classNameBindings: ['_isDeletingNote:SH-c-seller-profile-notes-item--deleting-note'],

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);

    //set dynamic properties that require the note id on initialization.
    defineProperty(this, '_isSavingNote', readOnly(`isSavingEditedNoteMap.${this.get('note.id')}`));
    defineProperty(this, '_isEditingNote', readOnly(`isEditingNoteMap.${this.get('note.id')}`));

    // set default note value
    this.set('_editingNoteValue', stripTags(convertBRsToNewLines(this.note.content)));
  },

  /**
   * @param {Note}
   */
  note: null,

  /** @param {User} user */
  user: null,

  maxNoteLength: undefined,

  /** @param {Ember.Object} isSavingEditedNoteMap */
  isSavingEditedNoteMap: null,

  /** @param {Ember.Object} isEditingNoteMap */
  isEditingNoteMap: null,

  /** @param {Function} toggleEditExistingNote */
  toggleEditExistingNote: () => {},

  /** @param {Function} updateNote */
  updateNote: () => {},

  /** @param {Function} deleteNote */
  deleteNote: () => {},

  /**
   * @type {Boolean}    True if the user can edit the note.
   */
  _canEditNote: readOnly('note.canEdit'),

  /**
   * This is the initial note value, or if the user is editing it, it will be updated to the new value that user
   * puts in the editor.
   *
   * @type {String}
   */
  _editingNoteValue: '',

  /**
   * @type {Boolean}    True when we're deleting a note.
   */
  _isDeletingNote: false,

  /**
   * NOTE: THIS IS SET DYNAMICALLY IN THE INIT METHOD OF THE COMPONENT.
   *
   * @type {Boolean}    True when the user is editing the note.
   */
  _isEditingNote: false,

  /**
   * NOTE: THIS IS SET DYNAMICALLY IN THE INIT METHOD OF THE COMPONENT.
   *
   * @type {Boolean}    True when the note is saving
   */
  _isSavingNote: false,

  /**
   * @type {String}     The created by name for the note, either 'you' or the user's name
   */
  _noteCreatedByName: computed('currentUser.id', 'note.createdByUser.id', function() {
    if (this.get('currentUser.id') === this.get('note.createdByUser.id')) {
      return 'You';
    }

    return this.get('note.createdByUser.fullName');
  }),

  actions: {
    /**
     * Called when user clicks the cancel action
     */
    onCancelEditNoteClicked: function() {
      this.toggleEditExistingNote(this.note.id, false);
      this.set('_editingNoteValue', this.note.content);
    },

    /**
     * When user clicks to delete the note.
     *
     * @param {MouseEvent} e
     */
    onDeleteNoteClicked: function(e) {
      e.stopPropagation();
      e.preventDefault();

      let data = {
        titleText: "Delete note",
        bodyText: `Are you sure you want to permanently delete this note?`,
        actionButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        isShowCancelButton: true,
        confirmAction: () => {
            this.set('_isDeletingNote', true);
            this.deleteNote(this.note).finally(() => {
              if (!this.isDestroying) {
                this.set('_isDeletingNote', false);
              }
            });
        }
      };

      showModal(this, 'confirmation', data);
    },

    /**
     * When user clicks to edit the note.
     *
     * @param {MouseEvent} e
     */
    onEditNoteClicked: function(e) {
      e.stopPropagation();
      e.preventDefault();

      this.get('toggleEditExistingNote')(this.get('note.id'), true);
    },

    onNoteValueInput: function(text) {
      this.set('_editingNoteValue', text);
    },

    /**
     * Called when user clicks the save action
     *
     * @param {String} noteValue      The new note after user clicks save
     */
    onSaveEditNoteClicked: function(noteValue) {
      let newNoteValue = convertNewLinesToBRs(noteValue);
      if (newNoteValue === this.get('note.content')) {
        //no work to do, didn't change
        this.get('toggleEditExistingNote')(this.get('note.id'), false);
        return Promise.resolve(true);
      }

      return this.get('updateNote')(this.get('note'), newNoteValue).then(() => {
        this.get('toggleEditExistingNote')(this.get('note.id'), false);
      }).catch(e => {
        handleErrorResponseWithModal(this, e);
      });
    },
  }
});
